import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProfileView, SignatureImageUpload, SignatureStyle, UpdateProfileInput, UserPreferences, UserSessionRow } from '@pqrs/contracts';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/api/client';
import { useSession } from '@/app/session';

/** «Mi perfil»: siempre la cuenta de la sesión (`/me/...`). */
export const profileKeys = { profile: ['me', 'profile'] as const, sessions: ['me', 'sessions'] as const };

export const useProfile = () => useQuery({ queryKey: profileKeys.profile, queryFn: () => apiGet<ProfileView>('/me/profile') });
export const useMySessions = () => useQuery({ queryKey: profileKeys.sessions, queryFn: () => apiGet<UserSessionRow[]>('/me/sessions') });

/** Tras cambiar algo de la cuenta: el perfil y lo que muestra `/auth/me` (cargo, preferencias). */
function useRefreshAccount() {
  const qc = useQueryClient();
  const { refresh } = useSession();
  return () => { void qc.invalidateQueries({ queryKey: ['me'] }); void refresh(); };
}

export function useUpdateProfile() {
  const done = useRefreshAccount();
  return useMutation({ mutationFn: (b: UpdateProfileInput) => apiPatch<ProfileView>('/me/profile', b), onSuccess: done });
}
/** Cómo firma sus respuestas: el API devuelve el perfil completo. */
export function useSignatureStyle() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (style: SignatureStyle) => apiPut<ProfileView>('/me/signature', { style }), onSuccess: (p) => qc.setQueryData(profileKeys.profile, p) });
}
export function useSignatureImage() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: SignatureImageUpload) => apiPut<ProfileView>('/me/signature/image', b), onSuccess: (p) => qc.setQueryData(profileKeys.profile, p) });
}
export function useRemoveSignatureImage() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => apiDelete<ProfileView>('/me/signature/image'), onSuccess: (p) => qc.setQueryData(profileKeys.profile, p) });
}
export function useCloseSession() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiDelete(`/me/sessions/${id}`), onSuccess: () => void qc.invalidateQueries({ queryKey: profileKeys.sessions }) });
}
export function useCloseOtherSessions() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => apiPost('/me/sessions/close-others'), onSuccess: () => void qc.invalidateQueries({ queryKey: profileKeys.sessions }) });
}

/**
 * Columnas visibles (y su orden) de una tabla, guardadas en el perfil de la persona (`preferences.tables[clave]`):
 * la misma elección en cualquier navegador. Se responde al instante con el valor local y se guarda detrás; un
 * valor viejo en `localStorage` (de antes de existir la preferencia) se adopta la primera vez.
 */
export function useTableColumns(clave: string, porDefecto: string[]): [string[], (columns: string[]) => void] {
  const { me } = useSession();
  const update = useUpdateProfile();
  const tablas = (me?.preferences as UserPreferences | undefined)?.tables;
  const guardadas = tablas?.[clave]?.columns;
  const [local, setLocal] = useState<string[] | null>(null);
  const migrado = useRef(false);
  const mutate = update.mutate;
  const guardar = useCallback((columns: string[]) => {
    setLocal(columns);
    mutate({ preferences: { tables: { ...(tablas ?? {}), [clave]: { columns } } } });
  }, [mutate, tablas, clave]);
  useEffect(() => {
    if (!me || guardadas || migrado.current) return;
    migrado.current = true;
    try {
      const raw = localStorage.getItem(`pqrs.${clave}.columns`);
      const viejas = raw ? (JSON.parse(raw) as unknown) : null;
      if (Array.isArray(viejas) && viejas.every((c) => typeof c === 'string') && viejas.length) { guardar(viejas); localStorage.removeItem(`pqrs.${clave}.columns`); }
    } catch { /* sin almacenamiento local */ }
  }, [me, guardadas, clave, guardar]);
  return [local ?? guardadas ?? porDefecto, guardar];
}
